import type { JSX } from 'react';


export type DeliverabilityClass = 'normal' | 'degraded' | 'blocked';

export interface DeliverabilityOption {
  action: string;
  actor: 'client' | 'platform' | 'operator';
  consequence: string;
  trade_off: string;
}

export interface DeliverabilityConflict {
  requirement_needs: string;
  platform_provides: string;
  limited_by: 'operating_system' | 'platform';
  prevents_delivery: boolean;
  evidence_file: string;
  evidence_line: number;
  options: ReadonlyArray<DeliverabilityOption>;
}

export interface DeliverabilityAssessment {
  what_is_being_delivered: string;
  delivery_requires_execution: boolean;
  conflicts: ReadonlyArray<DeliverabilityConflict>;
}

export interface DeliverabilityPayload {
  classification: DeliverabilityClass;
  assessment: DeliverabilityAssessment;
}

const WHO: Record<DeliverabilityOption['actor'], string> = {
  client: 'Your team',
  platform: 'This platform',
  operator: 'You'
};

const WHERE: Record<DeliverabilityConflict['limited_by'], string> = {
  operating_system: 'Limited by the operating system this platform runs on',
  platform: 'Limited by how this platform is set up'
};

export function DeliverabilityWarning({
  deliverability
}: {
  deliverability: DeliverabilityPayload | undefined;
}): JSX.Element | null {
  const assessment = deliverability?.assessment;
  if (
    deliverability === undefined ||
    deliverability.classification === 'normal' ||
    assessment === undefined ||
    assessment.conflicts.length === 0
  ) {
    return null;
  }
  const blocked = deliverability.classification === 'blocked';

  return (
    <section
      data-testid="deliverability-warning"
      className={`mx-4 mb-3 rounded-lg border overflow-hidden ${
        blocked ? 'border-err/40' : 'border-warn/40'
      }`}
    >
      <header
        className={`px-3 py-2 flex items-center gap-2 border-b ${
          blocked ? 'border-err/30 bg-err/10' : 'border-warn/30 bg-warn/10'
        }`}
      >
        <span
          className={`text-[11px] px-2 py-0.5 rounded-[10px] font-medium ${
            blocked ? 'bg-err text-white' : 'bg-warn/20 text-warn'
          }`}
        >
          {blocked ? 'CANNOT BE DELIVERED' : 'PARTLY DELIVERABLE'}
        </span>
        <span className="text-[13px] font-semibold">{assessment.what_is_being_delivered}</span>
      </header>

      <div className="px-3 py-3 flex flex-col gap-4">
        {assessment.conflicts.map((c, i) => (
          <div key={`${c.evidence_file}-${i}`} className="flex flex-col gap-2">
            <div
              className={`px-3 py-2 rounded border-l-[3px] ${
                c.prevents_delivery ? 'border-err bg-err/10' : 'border-warn bg-warn/10'
              }`}
            >
              <div
                className={`text-[12px] font-semibold ${
                  c.prevents_delivery ? 'text-err' : 'text-warn'
                }`}
              >
                {WHERE[c.limited_by]}
              </div>
              <div className="text-[13px] mt-1">
                <span className="font-medium">Needs:</span> {c.requirement_needs}
              </div>
              <div className="text-[13px] mt-0.5">
                <span className="font-medium">Available here:</span> {c.platform_provides}
              </div>
            </div>

            <div className="text-[11px] opacity-60">
              Found in:{' '}
              <code className="font-mono select-all break-all opacity-90">
                {c.evidence_line > 0 ? `${c.evidence_file}:${c.evidence_line}` : c.evidence_file}
              </code>
            </div>

            {c.options.length > 0 ? (
              <div>
                <div className="text-[12px] font-medium">Ways to resolve this</div>
                <ol className="mt-1 flex flex-col gap-2 list-decimal list-inside">
                  {c.options.map((o, j) => (
                    <li key={j} className="text-[12px]">
                      {o.action}
                      <div className="ml-4 mt-0.5 text-[11px] opacity-70">
                        <span className="font-medium">{WHO[o.actor]}</span> would do this ·{' '}
                        {o.consequence}
                      </div>
                      {o.trade_off.length > 0 ? (
                        <div className="ml-4 text-[11px] opacity-55">{o.trade_off}</div>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ))}

        <div
          className={`text-[12px] px-3 py-2 rounded ${
            blocked ? 'bg-err/10 text-err' : 'bg-warn/10 text-warn'
          }`}
        >
          {blocked ? (
            <>
              If you continue, the run will use <strong>time</strong> and{' '}
              <strong>paid AI credits</strong> and still will not produce what was asked for.
            </>
          ) : (
            <>
              If you continue, every stage will run and use <strong>time</strong> and{' '}
              <strong>paid AI credits</strong>. You will receive the finished work, checked for
              correctness, but <strong>it will not be run and proven here</strong>.
            </>
          )}
        </div>
      </div>
    </section>
  );
}
